<script setup lang="ts">
/**
 * PermissionsDrawer — per-user privilege editor.
 *
 * Mounted as a Drawer at size="lg" (~720px) with a Tabs strip
 * organizing privileges by domain. Each tab shows a checkbox grid
 * for one domain; the drawer's footer commits the entire set in
 * one PATCH call.
 *
 * Privilege grouping: backend returns a flat Map<String, Boolean>
 * with every privilege the system defines. The SPA classifies them
 * into six domain buckets via prefix matching:
 *
 *   iam        → users, add_users, delete_users, reset_user_password,
 *                 user_group, check_users
 *   catalog    → videos, categories, bundles
 *   commerce   → packages, prices
 *   subscriber → subscribers, subscribe_users (and any future Sprint 4
 *                privileges)
 *   marketing  → offers, feedback, paid_errors
 *   system     → admin (and any unrecognized — fallback bucket)
 *
 * If the backend introduces a privilege that doesn't fit any prefix
 * we know about, it falls into "system" rather than vanishing.
 */
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToastStore } from '@/stores/toast'
import Drawer from '@/components/ui/Drawer.vue'
import Tabs, { type TabItem } from '@/components/ui/Tabs.vue'
import Button from '@/components/ui/Button.vue'
import Spinner from '@/components/ui/Spinner.vue'
import Checkbox from '@/components/ui/Checkbox.vue'
import { usePermissionsQuery, useUpdatePermissionsMutation } from '../queries'
import type { UserView } from '../api'

interface Props {
  user: UserView | null
}
const props = defineProps<Props>()

const open = defineModel<boolean>('open', { required: true })

const { t } = useI18n()
const toast = useToastStore()

// -----------------------------------------------------------------
// Domain classification
// -----------------------------------------------------------------

type DomainId = 'iam' | 'catalog' | 'commerce' | 'subscriber' | 'marketing' | 'system'

interface DomainBucket {
  id: DomainId
  /** Privilege names that belong to this domain (bare names). */
  matchers: readonly string[]
}

const DOMAINS: readonly DomainBucket[] = Object.freeze([
  {
    id: 'iam',
    matchers: ['users', 'add_users', 'delete_users', 'reset_user_password', 'user_group', 'check_users'],
  },
  { id: 'catalog', matchers: ['videos', 'categories', 'bundles'] },
  { id: 'commerce', matchers: ['packages', 'prices'] },
  { id: 'subscriber', matchers: ['subscribers', 'subscribe_users', 'devices'] },
  { id: 'marketing', matchers: ['offers', 'feedback', 'paid_errors', 'campaigns'] },
  { id: 'system', matchers: ['admin'] },
])

function classify(privilege: string): DomainId {
  for (const domain of DOMAINS) {
    if (domain.matchers.includes(privilege)) return domain.id
  }
  // Unknown privileges fall into 'system' so they remain editable.
  return 'system'
}

// -----------------------------------------------------------------
// State
// -----------------------------------------------------------------

const userName = computed(() => props.user?.userName ?? '')
const permissionsQuery = usePermissionsQuery(() => props.user?.userName)
const updatePermissions = useUpdatePermissionsMutation(userName.value)

// Working copy of the permission map. Initialized from the query
// result; reset when a different user is loaded.
const draft = ref<Record<string, boolean>>({})

watch(
  () => permissionsQuery.data.value,
  (data) => {
    if (data) {
      draft.value = { ...data.permissions }
    }
  },
  { immediate: true },
)

// Reset draft state when modal closes (so re-opening shows fresh data).
watch(open, (isOpen) => {
  if (!isOpen) draft.value = {}
})

// -----------------------------------------------------------------
// Domain-grouped views
// -----------------------------------------------------------------

interface DomainGroup {
  id: DomainId
  privileges: { name: string; granted: boolean }[]
  grantedCount: number
}

const grouped = computed<DomainGroup[]>(() => {
  const buckets = new Map<DomainId, DomainGroup>()
  for (const domain of DOMAINS) {
    buckets.set(domain.id, { id: domain.id, privileges: [], grantedCount: 0 })
  }

  for (const [name, granted] of Object.entries(draft.value)) {
    const id = classify(name)
    let bucket = buckets.get(id)
    if (!bucket) {
      bucket = { id, privileges: [], grantedCount: 0 }
      buckets.set(id, bucket)
    }
    bucket.privileges.push({ name, granted })
    if (granted) bucket.grantedCount++
  }

  // Stable alphabetical ordering inside each bucket.
  for (const bucket of buckets.values()) {
    bucket.privileges.sort((a, b) => a.name.localeCompare(b.name))
  }

  // Drop empty domains entirely (e.g. if backend has no marketing
  // privileges yet, don't show an empty marketing tab).
  return [...buckets.values()].filter((b) => b.privileges.length > 0)
})

const tabs = computed<TabItem[]>(() =>
  grouped.value.map((g) => ({
    value: g.id,
    label: t(`iam.permissions.domain.${g.id}`),
    badge: g.grantedCount > 0 ? `${g.grantedCount}` : undefined,
  })),
)

const activeTab = ref<DomainId>('iam')
// Make sure the default tab is one that actually has content.
watch(grouped, (groups) => {
  if (groups.length === 0) return
  if (!groups.some((g) => g.id === activeTab.value)) {
    activeTab.value = groups[0]!.id
  }
}, { immediate: true })

// -----------------------------------------------------------------
// Mutations
// -----------------------------------------------------------------

function togglePrivilege(name: string, granted: boolean): void {
  draft.value = { ...draft.value, [name]: granted }
}

function toggleAll(domain: DomainId, granted: boolean): void {
  const next = { ...draft.value }
  for (const p of grouped.value.find((g) => g.id === domain)?.privileges ?? []) {
    next[p.name] = granted
  }
  draft.value = next
}

const isDirty = computed(() => {
  const initial = permissionsQuery.data.value?.permissions
  if (!initial) return false
  for (const [name, granted] of Object.entries(draft.value)) {
    if (initial[name] !== granted) return true
  }
  return false
})

async function save(): Promise<void> {
  await updatePermissions.mutateAsync({ permissions: draft.value })
  toast.push({
    kind: 'success',
    message: t('iam.permissions.success', { name: userName.value }),
  })
  open.value = false
}

function close(): void {
  open.value = false
}
</script>

<template>
  <Drawer
    v-model:open="open"
    size="lg"
    :title="$t('iam.permissions.title', { name: userName })"
    :subtitle="$t('iam.permissions.subtitle')"
  >
    <div
      v-if="permissionsQuery.isLoading.value"
      class="flex items-center justify-center py-12"
    >
      <Spinner size="lg" />
    </div>

    <div v-else-if="grouped.length === 0" class="py-8 text-center text-sm text-text-3">
      {{ $t('iam.permissions.empty') }}
    </div>

    <Tabs v-else v-model:value="activeTab" :tabs="tabs">
      <template
        v-for="group in grouped"
        :key="group.id"
        #[`panel-${group.id}`]
      >
        <div>
          <!-- Domain header with toggle-all helpers -->
          <div
            class="flex items-center justify-between mb-3 pb-2 border-b border-border"
          >
            <p class="text-xs text-text-3 m-0">
              {{ $t('iam.permissions.domainHelper', {
                granted: group.grantedCount,
                total: group.privileges.length,
              }) }}
            </p>
            <div class="flex gap-2">
              <Button kind="ghost" size="sm" @click="toggleAll(group.id, true)">
                {{ $t('iam.permissions.grantAll') }}
              </Button>
              <Button kind="ghost" size="sm" @click="toggleAll(group.id, false)">
                {{ $t('iam.permissions.revokeAll') }}
              </Button>
            </div>
          </div>

          <!-- Privilege grid: 2 columns on lg, 1 on smaller widths -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
            <label
              v-for="p in group.privileges"
              :key="p.name"
              class="flex items-center gap-2.5 px-2 py-1.5 rounded
                     hover:bg-surface-2 transition-colors cursor-pointer"
            >
              <Checkbox
                :model-value="p.granted"
                @update:model-value="(v) => togglePrivilege(p.name, v === true)"
              />
              <span class="text-xs font-mono text-text-2">{{ p.name }}</span>
            </label>
          </div>
        </div>
      </template>
    </Tabs>

    <template #footer>
      <Button kind="ghost" @click="close">
        {{ $t('actions.cancel') }}
      </Button>
      <Button
        kind="primary"
        :loading="updatePermissions.isPending.value"
        :disabled="!isDirty"
        @click="save"
      >
        {{ $t('iam.permissions.save') }}
      </Button>
    </template>
  </Drawer>
</template>
